import { AlertCircle, Undo2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { CANCEL_REASON_MAX_LENGTH } from "../constants/payoutRescue.constants";
import { PaymentsPayoutService } from "../services/payoutRescue.service";
import type { RescuePayout } from "../types/payoutRescue.types";
import { formatMoney, shortId } from "../utils/payoutRescueFormat";

export interface CancelPayoutModalProps {
  payout: RescuePayout;
  /** Resolved organization name (null when the catalog misses). */
  orgName: string | null;
  open: boolean;
  onClose: () => void;
  /** Fired with the fresh row (CANCELLED — or the racer's terminal state) so the page reconciles. */
  onResolved: (payout: RescuePayout, wasRace: boolean) => void;
}

const schema = z.object({
  reason: z.string().max(CANCEL_REASON_MAX_LENGTH).optional(),
});
type FormValues = z.infer<typeof schema>;
const DEFAULTS: FormValues = { reason: "" };

/**
 * Cancel a stuck-CREATED payout: the reserved funds (GROSS — that is what the wallet blocked) return to the
 * org's available balance atomically. Only legal from CREATED — a 400 here means someone else already
 * dispatched/resolved it (row-level race): the modal fetches the fresh row and resolves as a non-error
 * "already processed" notice, never a scary failure.
 */
export function CancelPayoutModal({ payout, orgName, open, onClose, onResolved }: CancelPayoutModalProps) {
  const { t, activeLanguage } = useTranslations("payoutRescue");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useZodForm(schema, DEFAULTS);

  const handleClose = () => {
    form.reset(DEFAULTS);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    const reason = values.reason?.trim() || undefined;
    try {
      const { data } = await PaymentsPayoutService.cancelPayout(payout.id, reason);
      form.reset(DEFAULTS);
      onResolved(data as RescuePayout, false);
      onClose();
    } catch (err: unknown) {
      // 400 illegal_transition = the payout left CREATED under us (another operator dispatched it). Fetch
      // the fresh row and surface it as an informative race, not an error.
      if (HttpApiService.isAxiosError(err) && err.response?.status === 400) {
        try {
          const { data } = await PaymentsPayoutService.getPayout(payout.id);
          form.reset(DEFAULTS);
          onResolved(data as RescuePayout, true);
          onClose();
          return;
        } catch {
          // Fall through to the generic error below.
        }
      }
      setSubmitError(HttpApiService.getErrorMessage(err) || t("cancel_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const gross = `${formatMoney(payout.gross_amount, activeLanguage)} ${payout.currency}`;

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) handleClose();
      }}
      title={t("cancel_title")}
      description={t("cancel_body", { gross, org: orgName ?? shortId(payout.organization_id) })}
    >
      <div className="space-y-4">
        {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}
        <ColocaForm form={form} onSubmit={handleSubmit}>
          <ColocaTextareaField
            name="reason"
            label={t("cancel_reason_label")}
            placeholder={t("cancel_reason_placeholder")}
            rows={3}
            maxLength={CANCEL_REASON_MAX_LENGTH}
            count
          />
          <div className="flex justify-end gap-2 pt-2">
            <ColocaButton type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("cancel_back")}
            </ColocaButton>
            <ColocaButton type="submit" variant="destructive" icon={Undo2} loading={isSubmitting}>
              {t("cancel_confirm")}
            </ColocaButton>
          </div>
        </ColocaForm>
      </div>
    </ColocaDialog>
  );
}
